<template>
  <div class="settings-view">
    <PageHeader title="General" subtitle="Workspace-wide settings. Applies to everyone." />
    <div v-if="message" class="workspace-alert">{{ message }}</div>
    <AsyncState :loading="!settings">
      <div class="settings-form-card">
        <div class="setting-row">
          <div class="setting-row-head">
            <div class="setting-row-label">Knowledge base title</div>
            <div class="setting-row-desc">Shown in the top bar and the browser tab.</div>
          </div>
          <div class="setting-control">
            <input v-model="title" class="setting-input" />
          </div>
        </div>
        <div class="setting-row">
          <div class="setting-row-head">
            <div class="setting-row-label">User registration</div>
            <div class="setting-row-desc">Allow visitors to create their own accounts.</div>
          </div>
          <div class="setting-control">
            <ToggleSwitch v-model="registrationEnabled">Enable self-service registration</ToggleSwitch>
          </div>
        </div>
        <div class="settings-save-bar">
          <span v-if="dirty" class="settings-save-hint dirty">Unsaved changes</span>
          <span v-else class="settings-save-hint">All changes saved</span>
          <button type="button" class="btn" :disabled="!dirty" @click="discard">Discard</button>
          <button type="button" class="btn btn-primary" :disabled="!dirty" @click="save">Save</button>
        </div>
      </div>
    </AsyncState>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import {
  CommandsService,
  type GetGlobalSettingsQueryResponse
} from '../api/generated';
import AsyncState from '../components/settings/AsyncState.vue';
import PageHeader from '../components/settings/PageHeader.vue';
import ToggleSwitch from '../components/settings/ToggleSwitch.vue';
import { getErrorMessage, useSessionStore } from '../stores/session';

const session = useSessionStore();
const settings = ref<GetGlobalSettingsQueryResponse | null>(null);
const title = ref('');
const registrationEnabled = ref(false);
const message = ref<string | null>(null);

const dirty = computed(() =>
  settings.value != null
  && (title.value !== settings.value.knowledgeBaseTitle
    || registrationEnabled.value !== settings.value.userRegistrationEnabled));

onMounted(load);

async function load(): Promise<void> {
  try {
    applySettings(session.pageBootstrap?.globalSettings ?? null);
    message.value = null;
  } catch (error) {
    message.value = getErrorMessage(error, 'Unable to load settings.');
  }
}

function applySettings(value: GetGlobalSettingsQueryResponse | null): void {
  if (value == null) {
    throw new Error('Settings bootstrap payload was missing.');
  }

  settings.value = value;
  title.value = value.knowledgeBaseTitle;
  registrationEnabled.value = value.userRegistrationEnabled;
}

function discard(): void {
  if (settings.value) {
    title.value = settings.value.knowledgeBaseTitle;
    registrationEnabled.value = settings.value.userRegistrationEnabled;
  }
}

async function save(): Promise<void> {
  try {
    const result = await CommandsService.commandUpdateGlobalSettingsCommand({
      requestBody: {
        knowledgeBaseTitle: title.value,
        userRegistrationEnabled: registrationEnabled.value
      }
    });
    applySettings(result);
    session.publicSettings = {
      knowledgeBaseTitle: result.knowledgeBaseTitle,
      userRegistrationEnabled: result.userRegistrationEnabled
    };
    message.value = 'Settings saved.';
  } catch (error) {
    message.value = getErrorMessage(error, 'Unable to save settings.');
  }
}
</script>
